import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import size from "lodash/size";
import sum from "lodash/sum";
import React from "react";
import { useStatus } from "util/useStatus";

const ConnectionChecker = () => {
  const { isLocal, isOnline, isSyncing } = useStatus();
  const [running, setRunning] = React.useState(false);
  const [pingResultsNorthAmerica, setPingResultsNorthAmerica] = React.useState<
    number[]
  >([]);
  const [pingResultsBackup, setPingResultsBackup] = React.useState<number[]>(
    []
  );

  const [error, setError] = React.useState("");

  const checkConnection = async () => {
    setRunning(true);
    setPingResultsNorthAmerica([]);
    setPingResultsBackup([]);
    try {
      for (let i = 0; i <= 40; i++) {
        await ping({
          couchServerUrl: "https://couchdb.liftingcast.com",
          setPingResults: setPingResultsNorthAmerica,
          index: i,
        });
      }
      for (let i = 0; i <= 40; i++) {
        await ping({
          couchServerUrl: "https://couchdb.backup.liftingcast.com",
          setPingResults: setPingResultsBackup,
          index: i,
        });
      }
    } catch (e) {
      console.log(e);
      setError("Connection Failed");
    }

    setRunning(false);
  };

  const ping = async ({
    couchServerUrl,
    setPingResults,
    index,
  }: {
    couchServerUrl: string;
    setPingResults: React.Dispatch<React.SetStateAction<number[]>>;
    index: number;
  }) => {
    const startTime = performance.now();
    const response = await fetch(`${couchServerUrl}`);
    if (response.ok) {
      await response.json();
    } else {
      throw new Error("connection failed");
    }
    const endTime = performance.now();
    const result = endTime - startTime;
    if (index !== 0) {
      setPingResults((prev) => [...prev, result]);
    }
  };

  return (
    <div className="connection-checker">
      <br />
      {isLocal && !isOnline && <div>This is an offline only meet.</div>}
      {isLocal && isOnline && isSyncing && (
        <div>
          This is an online meet. You are currently syncing data online.
        </div>
      )}
      {isLocal && isOnline && !isSyncing && (
        <div>
          This is an online meet. You are currently NOT syncing data online.
        </div>
      )}
      <br />
      <hr />
      <br />
      Your connection stability is important for running a meet across multiple
      computers. Especially important if using tablets to control ref lights.
      Run this test a few times to make sure you get consistent results. Keep in
      mind that if you are sharing wifi at the venue with spectators the results
      could change drastically when the venue is full.
      <br />
      <br />
      <button onClick={checkConnection} disabled={running}>
        Check Connection
      </button>
      <div>
        <br />
        {!!error ? (
          <div>{error}</div>
        ) : (
          <>
            <label>liftingcast.com Score: </label>
            {running ? (
              "checking..."
            ) : (
              <Result pingResults={pingResultsNorthAmerica} />
            )}
            <br />
            <label>backup.liftingcast.com Score: </label>
            {running ? (
              "checking..."
            ) : (
              <Result pingResults={pingResultsBackup} />
            )}
          </>
        )}
        <br />
        <ul>
          <li>
            <label>0 - 3</label> Poor: Not suitable for onsite live updates. Not
            suitable for ref light tablets. Recommend running multiple screens
            off one computer per platform.
          </li>
          <li>
            <label>4 - 6</label> OK: Suitable for onsite live updates. Expect
            delays with tablet lights.
          </li>
          <li>
            <label>7 - 8</label> Good: Suitable for onsite live updates.
          </li>
          <li>
            <label>8 - 10</label> Excellent: Suitable for onsite live updates.
            Tablet lights should work well.
          </li>
        </ul>
      </div>
    </div>
  );
};

const Result = ({ pingResults }: { pingResults: number[] }) => {
  if (!pingResults.length) {
    return null;
  }

  const total = sum(pingResults);
  const average = total / size(pingResults);

  const variation = Math.sqrt(
    sum(map(pingResults, (i) => Math.pow(i - average, 2))) / pingResults.length
  );

  let score: number | string = 0;

  if (average < 35) {
    score = 7;
  } else if (average < 75) {
    score = 6;
  } else if (average < 125) {
    score = 5;
  } else if (average < 275) {
    score = 4;
  } else if (average < 375) {
    score = 3;
  } else if (average < 450) {
    score = 2;
  } else if (average < 600) {
    score = 1;
  }

  if (variation < 6) {
    score += 3;
  } else if (variation < 15) {
    score += 2;
  } else if (variation < 25) {
    score += 1;
  }

  if (isEmpty(pingResults)) {
    score = "";
  }
  return <>{score}</>;
};

export default ConnectionChecker;
